import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import ReactImageMagnify from 'react-image-magnify';
import classNames from 'classnames';

import { modelOf } from '../../../prop-types';
import ProductImageModel from '../../../models/ProductImage';
import ImagePreload from '../../common/ImagePreload';
import ConfigStore from '../../../store/ConfigStore';
import TranslationStore from '../../../store/TranslationStore';
import TranslatedImageType from '../../../types/TranslatedImageType';

const DEFAULT_MIN_RATIO = 5 / 2;
const DEFAULT_PADDING_FOR_WIDE_IMAGES = 100;
const defaultImagePath = '/img/no_image_available.png';

@observer
export class ProductImage extends Component {
  imageElement = null;
  state = {
    fullImageLoaded: false,
    fullImageWidth: 0,
    fullImageHeight: 0,
    imagePadding: false
  };

  createProductImageModel = (imagePath) => {
    return ProductImageModel.create({
      id: 1,
      product_id: '',
      sizes: {
        mini: imagePath,
        small: imagePath,
        medium: imagePath,
        large: imagePath,
        xlarge: imagePath,
        full: imagePath
      },
      description: ''
    });
  };

  onImagePreload = ({ target: img }) => {
    this.setState({
      fullImageLoaded: true,
      fullImageWidth: img.offsetWidth,
      fullImageHeight: img.offsetHeight,
      imagePadding: this.needsPadding(img)
    });
  };

  getImageAlt = () => {
    const { product, configStore } = this.props;
    const productImage = this.getProductImage();
    const description = configStore.useImageDescriptionAsAlt
      ? productImage.description || product.name
      : product.name;

    return [
      description,
      product && product.mainCategory ? product.mainCategory.name : '',
      product.id,
      productImage.id
    ].join(' - ');
  };

  getProductImage = () => {
    return this.props.productImage || this.getDefaultImage();
  };

  getDefaultImage = () => {
    const { translationStore } = this.props;
    const defaultImage = translationStore.translatedImages.find(
      (image) => image.image_key === TranslatedImageType.IMAGE_NOT_FOUND_NAME
    );
    return defaultImage
      ? this.createProductImageModel(defaultImage.image_path)
      : this.createProductImageModel(defaultImagePath);
  };

  handleImageLoadError = (e) => {
    e.target.onerror = null;
    e.target.src = this.getImageSrc(
      this.createProductImageModel(defaultImagePath)
    );
  };

  getImageSrc = (productImage) => {
    const { size } = this.props;
    return productImage.sizes[size]
      ? productImage.sizes[size]
      : productImage.sizes['full'];
  };

  fullImageLargerThanElement = () => {
    return (
      this.imageElement &&
      (this.imageElement.clientWidth < this.state.fullImageWidth ||
        this.imageElement.clientHeight < this.state.fullImageHeight)
    );
  };

  needsPadding = (img) => {
    const { configStore } = this.props;
    // Padding is added when its width is 2.5 times its height
    if (
      this.imageElement &&
      Number(configStore.productImageAspectRatio) >= 100
    ) {
      return img.offsetWidth / img.offsetHeight > DEFAULT_MIN_RATIO;
    }
    return false;
  };

  getModifiedAspectRatio = () => {
    const {
      configStore,
      overrideConfigStoreAspectRatio,
      customAspectRatio
    } = this.props;

    if (this.state.fullImageLoaded) {
      const width = this.state.fullImageWidth;
      const height = this.state.fullImageHeight;
      if (width >= height) {
        return DEFAULT_PADDING_FOR_WIDE_IMAGES;
      }
    }

    if (overrideConfigStoreAspectRatio) {
      return customAspectRatio;
    } else {
      return configStore.productImageAspectRatio;
    }
  };

  render() {
    const {
      magnify,
      forceAspectRatio,
      className,
      wrapperClassName,
      overlayIcon,
      lazyLoading
    } = this.props;
    const productImage = this.getProductImage();
    const imageSrc = this.getImageSrc(productImage);
    const alt = this.getImageAlt();

    let imagePreload = '';
    if (magnify) {
      imagePreload = (
        <ImagePreload
          src={productImage.sizes['full']}
          onImageLoad={this.onImagePreload}
        />
      );
    }

    const lazyLoad = lazyLoading ? 'lazy' : '';
    let image;
    if (
      magnify &&
      this.state.fullImageLoaded &&
      this.fullImageLargerThanElement()
    ) {
      image = (
        <ReactImageMagnify
          fadeDurationInMs={150}
          hoverDelayInMs={0}
          className="ProductImage__small-image-container"
          style={{
            width: '100%',
            height: '100%'
          }}
          enlargedImageContainerClassName="ProductImage__magnified-image-container"
          enlargedImagePosition="over"
          smallImage={{
            alt,
            isFluidWidth: true,
            src: imageSrc
          }}
          largeImage={{
            alt,
            src: productImage.sizes['full'],
            width: this.state.fullImageWidth,
            height: this.state.fullImageHeight
          }}
        />
      );
    } else if (imageSrc) {
      image = (
        <img
          src={imageSrc}
          alt={alt}
          onError={this.handleImageLoadError}
          loading={lazyLoad}
        />
      );
    } else {
      image = null;
    }
    const style = forceAspectRatio
      ? {
          paddingBottom: this.getModifiedAspectRatio() + '%'
        }
      : null;

    return (
      <div
        className={classNames('ProductImage', className, {
          'ProductImage--force-aspect-ratio': forceAspectRatio
        })}
        style={style}
        ref={(ref) => (this.imageElement = ref)}
      >
        {imagePreload}
        <div
          className={classNames('ProductImage__wrapper', wrapperClassName, {
            'ProductImage__wrapper-wide': this.state.imagePadding
          })}
        >
          {image}
        </div>
        {overlayIcon}
      </div>
    );
  }
}

ProductImage.propTypes = {
  // Allows Product, ChildProduct and OrderProduct.
  product: PropTypes.object.isRequired,
  size: PropTypes.string.isRequired,
  className: PropTypes.string,
  wrapperClassName: PropTypes.string,
  translationStore: modelOf(TranslationStore),
  productImage: modelOf(ProductImageModel),
  configStore: modelOf(ConfigStore),
  magnify: PropTypes.bool,
  forceAspectRatio: PropTypes.bool,
  overrideConfigStoreAspectRatio: PropTypes.bool,
  customAspectRatio: PropTypes.string,
  overlayIcon: PropTypes.node,
  lazyLoading: PropTypes.bool
};

ProductImage.defaultProps = {
  forceAspectRatio: true,
  lazyLoading: true
};

export default inject('configStore', 'translationStore')(ProductImage);



// WEBPACK FOOTER //
// ./src/components/product/ProductImage/index.jsx